import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import PlanPriceBlock from "@/shared/components/UpsellPage/components/PlanPriceBlock";
import { zodFormResolver } from "@/shared/schemas/zodFormResolver";
import { Button } from "@/shared/ui/Button";
import { Checkbox } from "@/shared/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/Dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/Form";
import { Textarea } from "@/shared/ui/TextArea";
import { useCreateSubscriptionRequestMutation } from "../hooks/useCreateSubscriptionRequestMutation";
import {
  subscriptionRequestSchema,
  type SubscriptionRequestFormValues,
} from "../schemas/subscriptionRequest.schema";
import { SubscriptionRequestSuccess } from "./SubscriptionRequestSuccess";
import type {
  ActivePromotion,
  UpsellPlanPricing,
} from "../types/subscriptionRequest.types";

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  organizationName: string
  representativeFullName: string
  targetPlanCode: string
  targetPlanName: string
  promotion: ActivePromotion | null
  pricing: UpsellPlanPricing | undefined
  planHighlights: string[]
};

const DEFAULT_VALUES: SubscriptionRequestFormValues = {
  message: "",
  confirmed: false as unknown as true,
};

const PRIMARY_BUTTON_CLASSES =
  "rounded-full border-transparent bg-[#8775f9] text-white shadow-[0_1px_2px_rgba(0,0,0,0.08),0_8px_24px_rgba(139,116,255,0.30)] hover:bg-[#7361e7] hover:text-white active:scale-[0.98]";
const SECONDARY_BUTTON_CLASSES =
  "rounded-full border border-[rgba(0,0,0,0.12)] bg-white text-[#0a0a0a] shadow-none hover:bg-white hover:border-[rgba(0,0,0,0.24)] hover:text-[#0a0a0a] active:scale-[0.98]";

export const SubscriptionRequestModal = ({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  representativeFullName,
  targetPlanCode,
  targetPlanName,
  promotion,
  pricing,
}: Props) => {
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

  const form = useForm<SubscriptionRequestFormValues>({
    resolver: zodFormResolver(subscriptionRequestSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (open) {
      form.reset(DEFAULT_VALUES);
      setSubmittedSuccessfully(false);
    }
  }, [open, form]);

  const { mutateAsync, isPending } = useCreateSubscriptionRequestMutation({ organizationId });

  const handleSubmit = async (values: SubscriptionRequestFormValues) => {
    try {
      await mutateAsync({
        target_plan_code: targetPlanCode,
        message: values.message?.trim() || undefined,
        applied_promotion_id: promotion?.id,
      });
      setSubmittedSuccessfully(true);
    }
    catch {
      // mutation already surfaces error via axios interceptor
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl border-[rgba(0,0,0,0.06)] p-7">
        {submittedSuccessfully
          ? (
            <>
              <SubscriptionRequestSuccess />
              <DialogFooter>
                <Button
                  type="button"
                  className={PRIMARY_BUTTON_CLASSES}
                  onClick={() => onOpenChange(false)}
                >
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )
          : (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-light tracking-tight text-[#0a0a0a]">
                  {`Demande de souscription au ${targetPlanName}`}
                </DialogTitle>
              </DialogHeader>

              {pricing && (
                <div className="rounded-2xl border border-[rgba(0,0,0,0.08)] bg-white p-4">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#a1a1aa]">
                    {targetPlanName}
                  </p>
                  <PlanPriceBlock pricing={pricing} />
                  {promotion?.title && (
                    <p className="-mt-4 text-xs text-[#52525b]">
                      Offre
                      {" "}
                      <span className="font-medium">{promotion.title}</span>
                    </p>
                  )}
                </div>
              )}

              <div className="rounded-2xl bg-[#f4f2ff] p-4 text-sm text-[#0a0a0a]">
                <p>
                  <span className="font-medium">Vous : </span>
                  {representativeFullName}
                </p>
                <p className="mt-1">
                  <span className="font-medium">Pour : </span>
                  {organizationName}
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-[#0a0a0a]">
                          Message (optionnel)
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            rows={3}
                            maxLength={500}
                            placeholder="Précisez votre besoin, votre échéance…"
                            className="rounded-xl border-[rgba(0,0,0,0.12)] focus-visible:border-[#8775f9] focus-visible:ring-[#8775f9]/30"
                            {...field}
                          />
                        </FormControl>
                        <p className="text-right text-xs text-[#a1a1aa]">
                          {`${field.value?.length ?? 0}/500`}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmed"
                    render={({ field }) => (
                      <FormItem className="flex items-start gap-2.5 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value === true}
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)}
                          />
                        </FormControl>
                        <div className="leading-snug">
                          <FormLabel className="text-sm font-normal text-[#52525b]">
                            Je confirme être habilité à demander une évolution du
                            contrat pour mon CSE.
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />

                  <DialogFooter className="gap-2">
                    <Button
                      type="button"
                      className={SECONDARY_BUTTON_CLASSES}
                      onClick={() => onOpenChange(false)}
                      disabled={isPending}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      className={PRIMARY_BUTTON_CLASSES}
                      disabled={isPending || form.watch("confirmed") !== true}
                    >
                      {isPending && (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      )}
                      Envoyer ma demande
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
};
